import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useCurrencyCodes, COOKIE_NAME as CONTENT_CURRENCY_COOKIE } from 'src/core/state/currency';
import { currency } from 'src/queries/__generated__/currency';
import { clientStoreConfig } from 'src/queries/__generated__/clientStoreConfig';
import { useDeps } from 'src/hooks/useDeps';
import { ProductPrice } from 'src/types/global-types';
import { usePhoneNumber } from 'src/util/getPhoneNumber';
import Bugsnag from '@bugsnag/js';
import { toNumber } from 'es-toolkit/compat';
import { getBrowserCookies, cookieExpiresInDays, isIrishUserCookieValue } from 'src/util/cookies';

import { environments } from '../../env';
import currenciesQuery from '../../queries/currencies.graphql';
import clientStoreConfigQuery from '../../queries/clientStoreConfig.graphql';

const noop = (name: string) => {
    return () => {
        // eslint-disable-next-line no-console
        console.log(`${name} is missing - did you include the register function for it?`);
        return undefined;
    };
};

export type Currency = { code: string; symbol: string };
export type SetupCharge = ProductPrice;
export type Reviews = {
    average_rating: number;
    rated_as: string;
    total_reviews: number;
};

type ConfigContextType = {
    setCurrency: (code: string) => undefined;
    availableCurrencies: Currency[];
    currentCurrency: Currency;
    setupCharge: SetupCharge;
    furniture: {
        headerPhoneNumber: string | null;
    };
    liveChat: {
        enabled: boolean;
        apiKey: string | null;
    };
    gtm: {
        enabled: boolean;
        id: string | null;
    };
    klaviyo: {
        enabled: boolean;
        companyId: string | null;
    };
    leadfeeder: {
        enabled: boolean;
    };
    cutOffTime?: string[];
    superFastCutOffTime?: string[];
    blackoutDays?: Record<
        string,
        {
            blackout_date: string;
        }
    >;
    additionalDays: number;
};

const CURRENCY_BY_CODE: Record<string, Currency> = {
    GBP: { code: 'GBP', symbol: '£' },
    EUR: { code: 'EUR', symbol: '€' },
    USD: { code: 'USD', symbol: '$' },
};

/** Store-aware fallback — read cookies at call time (module-scope cookie reads are wrong on SSR). */
export function getStoreDefaultCurrency(): Currency {
    return isIrishUserCookieValue(getBrowserCookies().get('isIrishUser', { doNotParse: true }))
        ? CURRENCY_BY_CODE.EUR
        : CURRENCY_BY_CODE.GBP;
}

const initialState: ConfigContextType = {
    setCurrency: noop('setCurrency'),
    availableCurrencies: [
        { code: 'GBP', symbol: '£' },
        { code: 'EUR', symbol: '€' },
    ],
    currentCurrency: CURRENCY_BY_CODE.GBP,
    furniture: {
        headerPhoneNumber: null,
    },
    liveChat: {
        enabled: false,
        apiKey: null,
    },
    gtm: {
        enabled: false,
        id: null,
    },
    klaviyo: {
        enabled: false,
        companyId: null,
    },
    leadfeeder: {
        enabled: false,
    },
    setupCharge: {
        regular_price_incl: 0,
        regular_price: 0,
        final_price_incl: 0,
        final_price: 0,
        price: 0,
        price_incl: 0,
    },
    cutOffTime: [],
    superFastCutOffTime: [],
    blackoutDays: undefined,
    additionalDays: 0,
};

const isProd = process.env.NODE_ENV === 'production';
export const ipGeolocationApiKey: string | any = isProd
    ? environments['master'].IP_GEOLOCATION_API_KEY
    : environments['cypress'].IP_GEOLOCATION_API_KEY;

function resolveCurrencyCodeForStore(storeIsIrish: boolean, currencyOverride?: string): string {
    const urlParams = new URLSearchParams(window.location.search);
    // Prefer an explicit override (e.g. the `cur` param captured before the URL was stripped)
    // and fall back to the current URL so an explicit currency always wins over the store default.
    const currencyParam = currencyOverride?.toLowerCase() || urlParams.get('cur')?.toLowerCase();

    switch (currencyParam) {
        case 'eur':
            return 'EUR';
        case 'gbp':
            return 'GBP';
        case 'usd':
            return 'USD';
        default:
            return storeIsIrish ? 'EUR' : 'GBP';
    }
}

export const setCookie = (
    key: string,
    value: string,
    setCurrency: (currency: string) => void,
    reload?: boolean,
    currencyOverride?: string,
) => {
    getBrowserCookies().set(key, value, cookieExpiresInDays(3));
    const currencyCode = resolveCurrencyCodeForStore(value === 'true', currencyOverride);
    // Persist synchronously so a reload immediately after store switch still SSRs the right currency.
    getBrowserCookies().set(CONTENT_CURRENCY_COOKIE, currencyCode, cookieExpiresInDays(3));

    if (reload) {
        // Don't setCurrency before reload — that triggers product refetches and Oops UI.
        window.location.reload();
        return;
    }

    setCurrency(currencyCode);
};

export const setInitialGeoCookie = (ipGeolocationApiKey, setCookie, setCurrency: (currency: string) => void) => {
    fetch(`https://api.ipgeolocation.io/ipgeo?apiKey=${ipGeolocationApiKey}`, {
        method: 'GET',
        redirect: 'follow',
    })
        .then((response) => response.json())
        .then((result) => {
            if (result?.country_name === 'Ireland') {
                setCookie('isIrishUser', 'true', setCurrency, true);
            } else {
                setCookie('isIrishUser', 'false', setCurrency);
            }
        })
        .catch((error) => console.error(error));
};

export const ConfigContext = React.createContext<ConfigContextType>(initialState);

const EMPTY_ARR = [];
export const ConfigProvider: React.FC<React.PropsWithChildren> = React.memo((props) => {
    const { env } = useDeps();
    const { setCurrency, code, data } = useCurrencyCodes<currency>(currenciesQuery, env.SSR_GQL);
    // Livechat license is not needed for crawlable shell HTML — load client-side only.
    const { data: clientConfig } = useQuery<clientStoreConfig>(clientStoreConfigQuery, {
        ssr: false,
    });

    const getCookie = (key: string) => {
        return getBrowserCookies().get(key, { doNotParse: true });
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const currencyParam = urlParams.get('cur')?.toLowerCase();

        if (currencyParam) {
            switch (currencyParam) {
                case 'eur':
                    setCurrency('EUR');
                    break;
                case 'gbp':
                    setCurrency('GBP');
                    break;
                case 'usd':
                    setCurrency('USD');
                    break;
                default:
                    // If invalid currency parameter, do nothing
                    break;
            }
        }
    }, [setCurrency]);

    useEffect(() => {
        // Repair cookies written as JSON (`"GBP"`) by an earlier revision — those break hydrate.
        const rawContentCurrency = getCookie(CONTENT_CURRENCY_COOKIE);
        if (rawContentCurrency) {
            try {
                const parsed = JSON.parse(rawContentCurrency);
                if (typeof parsed === 'string' && CURRENCY_BY_CODE[parsed]) {
                    getBrowserCookies().set(CONTENT_CURRENCY_COOKIE, parsed, cookieExpiresInDays(3));
                    if (!code) {
                        setCurrency(parsed);
                    }
                }
            } catch {
                // Already a plain code (EUR/GBP/USD) — leave as-is.
            }
        }

        if (!getCookie('isIrishUser')) {
            setInitialGeoCookie(ipGeolocationApiKey, setCookie, setCurrency);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const availableCurrencies = useMemo((): Currency[] => {
        const symbols = data?.currencySymbols || EMPTY_ARR;
        // Magento may return null entries; accessing .code on those white-screens the app.
        const next: Currency[] = [];
        for (const item of symbols) {
            if (item?.code && item?.symbol) {
                next.push({ code: item.code, symbol: item.symbol });
            }
        }
        return next;
    }, [data]);

    const currentCurrency: Currency = useMemo(() => {
        const magentoDefaultCode = data?.currency?.default_display_currency_code;
        const magentoDefaultSymbol = data?.currency?.default_display_currency_symbol;
        const storeDefault = getStoreDefaultCurrency();
        // Redux code (from cookie) wins; else Magento store default (ireland → EUR); else cookie/store fallback.
        const resolvedCode = (code && CURRENCY_BY_CODE[code] ? code : null) || magentoDefaultCode || storeDefault.code;

        const match = availableCurrencies.find((a) => a.code === resolvedCode);
        if (match) {
            return match;
        }

        if (resolvedCode === magentoDefaultCode && magentoDefaultSymbol) {
            return { code: resolvedCode, symbol: magentoDefaultSymbol };
        }

        return CURRENCY_BY_CODE[resolvedCode] || storeDefault;
    }, [availableCurrencies, code, data]);

    const headerPhoneNumber = usePhoneNumber('spaced');
    const furniture = {
        headerPhoneNumber,
    };

    /**
     * Setup charges for customisations
     */
    const setupCharge: SetupCharge = useMemo(() => {
        if (!data) return initialState.setupCharge;
        const charge = data?.storeConfig?.highest_setup_charge;
        if (!charge) return initialState.setupCharge;
        return {
            regular_price_incl: 0,
            regular_price: 0,
            final_price_incl: 0,
            final_price: 0,
            price: charge.excl_tax ?? 0,
            price_incl: charge.incl_tax ?? 0,
        };
    }, [data]);

    const isProd = process.env.NODE_ENV === 'production';
    const liveChatEnabled = clientConfig?.storeConfig?.livechat_general_enabled === '1';

    const liveChat = {
        enabled: isProd && liveChatEnabled,
        apiKey: clientConfig?.storeConfig?.livechat_general_license || '',
    };

    const cutOffTime = data?.storeConfig?.cutoff_time;
    const superFastCutOffTime = data?.storeConfig?.superfast_cutoff_time;

    const gtm = {
        enabled: env.GTM_ENABLED,
        id: env.GTM_ID,
    };

    const klaviyo = {
        enabled: env.KLAVIYO_ENABLED || false,
        companyId: env.KLAVIYO_COMPANY_ID || null,
    };

    const leadfeeder = {
        enabled: env.LEADFEEDER_ENABLED || false,
    };

    let blackoutDays: ConfigContextType['blackoutDays'];
    try {
        blackoutDays = data?.storeConfig?.blackout_days ? JSON.parse(data.storeConfig.blackout_days) : undefined;
    } catch {
        blackoutDays = undefined;
    }

    const additionalDays = toNumber(data?.storeConfig?.extra_days_new_artwork);

    return (
        <ConfigContext.Provider
            value={{
                setCurrency,
                availableCurrencies,
                currentCurrency,
                furniture,
                liveChat,
                gtm,
                klaviyo,
                leadfeeder,
                setupCharge,
                cutOffTime,
                superFastCutOffTime,
                blackoutDays,
                additionalDays,
            }}
        >
            {props.children}
        </ConfigContext.Provider>
    );
});

export function useCurrencyChange(fn: (...args: []) => Promise<any>) {
    const { currentCurrency } = useContext(ConfigContext);
    const track = useRef<string | null>(null);
    const isFirstRun = useRef(true);
    const { code } = currentCurrency;
    useEffect(() => {
        // Skip the first resolution (SSR hydrate / cookie hydrate). Refetching here races
        // Magento and puts productDetailSlim into error state → Oops page.
        if (isFirstRun.current) {
            isFirstRun.current = false;
            track.current = code;
            return;
        }
        if (code && code !== track.current) {
            fn().catch((e) => {
                Bugsnag.notify('Error from refetch', (event) => {
                    event.addMetadata('errorObject', e);
                });
            });
            track.current = code;
        }
    }, [code, fn]);
}
